import type { ErrorParams } from '@jetopolis/shared';

/**
 * Тонкая обёртка над fetch. Единственная её задача - привести отказ сервера к
 * паре (код i18n, параметры), чтобы вызывающий код никогда не показывал игроку
 * сырой текст с сервера. Сервер его и не шлёт (инвариант 3), но клиент обязан
 * пережить и неожиданный ответ.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly params: ErrorParams | undefined;
  readonly status: number;

  constructor(code: string, status: number, params?: ErrorParams) {
    super(code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.params = params;
  }
}

interface Failure {
  ok: false;
  code: string;
  params?: ErrorParams;
}

interface Success<T> {
  ok: true;
  result: T;
}

function isFailure(body: unknown): body is Failure {
  return (
    typeof body === 'object' &&
    body !== null &&
    'ok' in body &&
    body.ok === false &&
    'code' in body &&
    typeof body.code === 'string'
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      // Сессия живёт в куке: без credentials сервер увидит гостя вместо игрока.
      credentials: 'include',
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError('errors.net.offline', 0);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok || isFailure(body)) {
    const code = isFailure(body) ? body.code : 'errors.common.internal';
    const params = isFailure(body) ? body.params : undefined;
    throw new ApiError(code, res.status, params);
  }

  if (typeof body === 'object' && body !== null && 'result' in body) {
    return (body as Success<T>).result;
  }
  return body as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, data?: unknown): Promise<T> =>
    request<T>(
      path,
      data === undefined ? { method: 'POST' } : { method: 'POST', body: JSON.stringify(data) },
    ),
  patch: <T>(path: string, data: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
};
